<script lang="ts">
import DownloadButton from '@/components/v2/DownloadButton.vue';
import CarrierGroupFilter from '@/components/CarrierGroupFilter.vue';
import CarrierSurveyViewOptions from '@/components/CarrierSurveyViewOptions.vue';
import YearFilter from '@/components/YearFilter.vue';

export default {
  name: 'CarrierSurvey',
  components: {
    DownloadButton,
    CarrierGroupFilter,
    CarrierSurveyViewOptions,
    YearFilter,
  },
};

interface Row {
  category: string;
  question: string;
  type: string;
  carriers: CarrierRowData[];
}
</script>

<script setup lang="ts">
import { carrierService, referenceService } from '@/services/api';
import {
  bubbleChartColors,
  chartHighlightColor,
  chartMutedColor,
  chartQuaternaryColor,
  colorScale,
  colorScaleFromValue,
} from '@/utilities/Color';
import { DownloadTableToCSV } from '@/utilities/Download';
import { onMounted, ref, computed, watch } from 'vue';
import {
  AnnotationItemType,
  CarrierDataTableType,
  CarrierRowData,
  CarrierSurveyResult,
  CarrierCategoryGroupResult,
  ChartDataType,
  ViewOptions,
  MinMaxValue,
  AxisDisplayMode,
} from '@/types/CarrierSurvey';
import { compactMoney } from '@/utilities/Money';
import GenericFilter from '@/components/GenericFilter.vue';
import { LabelFilterType } from '@/types/components/FilterType';
import { sanitizeHtml } from '@/utilities/Sanitization';
import { ExternalLink, ArrowLeft, Menu } from 'lucide-vue-next';
import '@/views/carrier/CarrierSurvey.vue';
import { useRouter } from 'vue-router';

// constants
// ----------
const AVERAGE = 'average score';
const SHOW_ALL = 'showAll';
const SHOW_NONE = 'showNone';
const SHOW_SELECTED = 'showSelected';
const SHOW_VALUES = 'show';
const STANDARD_BUBBLE = 'standardBubble';
const COUNT_BUBBLE = 'countBubble';
const PREMIUM_BUBBLE = 'premiumBubble';
const PRIMARY_PUBLIC = 'primaryPublic';
const PRIMARY_PRIVATE = 'primaryPrivate';
const LEAD_SIDE_A = 'leadSideA';
const EXCESS_D_AND_O = 'excessDAndO';
const VALUE_CAC_PARTNERSHIP = 'valueCACPartnership';
const DATA_LABEL_PADDING = '\n\n\n';
const BUBBLE_SIZE_RANGE_TOP = 100;

const SURVEY_TYPE_DNO = 'do';
const SURVEY_TYPE_TL = 'tl';
const SURVEY_TYPE_CYBER = 'cyber';

const SURVEY_TYPE_MAP = new Map<string, string>([
  [SURVEY_TYPE_DNO, 'D&O'],
  [SURVEY_TYPE_TL, 'Reps & Warranties'],
  [SURVEY_TYPE_CYBER, 'Cyber'],
]);

type CarrierAverageByYear = {
  year?: number;
  avgX: number;
  avgY: number;
};

const props = defineProps<{
  scenario: 'do' | 'tl' | 'cyber';
}>();

// refs
// -----
const router = useRouter();
const carrierSurveyData = ref<CarrierSurveyResult[] | undefined>(undefined);
const selectedCarriers = ref<string[]>([]);
const appliedCarriers = ref<string[]>([]);
const selectedYears = ref<number[]>([]);
const appliedYears = ref<number[]>([]);
const viewOptions = ref<ViewOptions>({
  labels: SHOW_ALL,
  carriers: SHOW_SELECTED,
  valueDisplay: SHOW_VALUES,
  bubbles: STANDARD_BUBBLE,
  axisDisplayMode: 'none',
});
const minXValue = ref(0);
const maxXValue = ref(0);
const minYValue = ref(0);
const maxYValue = ref(0);
const avgXValue = ref(0);
const avgYValue = ref(0);
const isExporting = ref(false);
const isComparing = ref(false);
const chart = ref<HTMLElement | any>();
const lockScale = ref(false);
const loading = ref(true);

const industries = ref<LabelFilterType<number>[]>([]);
const selectedIndustries = ref<number[]>([]);

const years = ref<number[]>([]);

const responderFocuses = ref<LabelFilterType<number>[]>([]);
const selectedResponderFocuses = ref<number[]>([]);

// computed
// ---------
const sortedYears = computed(() => [...selectedYears.value].sort());
const showValues = computed(() => viewOptions.value.valueDisplay === SHOW_VALUES);
const surveyType = computed(() => SURVEY_TYPE_MAP.get(props.scenario) ?? '');
const columnWidths = computed(() => {
  switch (props.scenario) {
    case SURVEY_TYPE_TL:
      return [3, 23, 35, 20];
    default:
      return [3, 20, 65, 20];
  }
});
const surveyTitle = computed(() => `${SURVEY_TYPE_MAP.get(props.scenario) ?? ''} Carrier Survey`);
const xAxisName = computed(() => 'Underwriting Execution');
const yAxisName = computed(() => {
  switch (props.scenario) {
    case SURVEY_TYPE_TL:
      return 'Policy Form & Administrative Execution';
    default:
      return 'Claims Execution';
  }
});
const xAxisCategories = computed(() => ['underwriting']);
const yAxisCategories = computed(() => {
  switch (props.scenario) {
    case SURVEY_TYPE_TL:
      return ['policy form', 'other / administrative'];
    default:
      return ['claims'];
  }
});
const latestYear = computed(() => {
  return Math.max(...appliedYears.value);
});

const surveyResultsCaption = computed(() => {
  const sortedYears = [...selectedYears.value].sort();
  if (isComparing.value) {
    let caption = 'Carrier Survey Result Comparison for ';
    for (let i = 0; i < sortedYears.length; i++) {
      let color = bubbleChartColors[i % bubbleChartColors.length];
      // Ensure year is a number to prevent injection
      const year = parseInt(String(sortedYears[i]));
      caption += `<span style="color: ${color}" class="[text-shadow:_0_1px_0_rgb(0_0_0_/_40%)]">${year}<span style="color: black">${i < sortedYears.length - 1 ? ', ' : ''}</span></span>`;
    }
    return sanitizeHtml(caption);
  }
  if (selectedYears.value.length === 0 || selectedYears.value.length > 3) {
    return 'Carrier Survey Results for all years';
  }
  // Sanitize years to prevent injection
  const sanitizedYears = sortedYears.map(y => parseInt(String(y))).join(', ');
  return sanitizeHtml(`Carrier Survey Results for ${sanitizedYears}`);
});

const dataSource = computed(() => {
  return {
    chart: {
      theme: 'cacs',
      showAlternateHGridColor: false,
      xaxisname: xAxisName.value + ' ➔',
      yaxisname: yAxisName.value + ' ➔' + (props.scenario === SURVEY_TYPE_DNO ? '{br}(* = No Data for Claims)' : ''),
      valueFontColor: '#2b2b2b',
      plottooltext: '$name',
      plotFillAlpha: 80,
      bubbleScale: bubbleScale.value,
      xAxisMinValue: minXValue.value,
      xAxisMaxValue: maxXValue.value,
      yAxisMinValue: minYValue.value,
      yAxisMaxValue: maxYValue.value,
      setAdaptiveXMin: false,
      setAdaptiveYMin: false,
      divLineAlpha: 0,
      numDivLines: 0,
      numVDivLines: 0,
      drawQuadrant: true,
      showYAxisValues: false,
      showXAxisValues: false,
      valueFontBold: '1',
      exportshowmenuitem: '0',
      baseFont: 'Verdana',
      captionFont: 'Verdana',
      xAxisNameFont: 'Verdana',
      yAxisNameFont: 'Verdana',
      textOutline: 1,
      quadrantxval: avgXValue.value,
      quadrantyval: avgYValue.value,
      animation: 0,
      ...chartPropertiesByAxisDisplayMode.value,
    },
    ...trendLinesByAxisDisplayMode.value,
    dataset: [{ data: chartData.value }],
    annotations: {
      showBelow: '1',
      groups: [
        {
          id: 'year-lines',
          items: chartYearLineAnnotations.value,
        },
      ],
    },
  };
});

/**
 * Provide conditional chart properties based on axis display mode.
 */
const chartPropertiesByAxisDisplayMode = computed(() => {
  if (viewOptions.value.axisDisplayMode === 'all') {
    return {
      showYAxisValues: true,
      showXAxisValues: true,
      xAxisValueDecimals: 2,
      yAxisValueDecimals: 2,
      forceXAxisValueDecimals: true,
      forceYAxisValueDecimals: true,
      numDivLines: 4,
      numVDivLines: 4,
    };
  }
  return {};
});

/**
 * Provide trend lines based on axis display mode.
 */
const trendLinesByAxisDisplayMode = computed(() => {
  switch (viewOptions.value.axisDisplayMode) {
    case 'averages':
      return {
        trendLines: [
          {
            line: [
              {
                startValue: avgYValue.value,
                displayValue: avgYValue.value.toFixed(2),
              },
            ],
          },
        ],
        vTrendLines: [
          {
            line: [
              {
                startValue: avgXValue.value,
                displayValue: avgXValue.value.toFixed(2),
              },
            ],
          },
        ],
      };
    case 'none':
    case 'all':
    default:
      return {};
  }
});

const bubbleScale = computed(() => {
  switch (viewOptions.value.bubbles) {
    case COUNT_BUBBLE:
      return props.scenario === SURVEY_TYPE_TL ? 0.4 : 0.3;
    case STANDARD_BUBBLE:
      return 0.2;
    default:
      return 1.0;
  }
});

const carriers = computed(() => {
  if (!Array.isArray(carrierSurveyData.value)) {
    return [];
  }
  return Object.keys(carrierSurveyData.value.reduce(groupByCarrierAndCategory, {}))
    .filter((carrier) => carrier !== null && carrier !== 'null')
    .sort((a, b) => a.localeCompare(b))
    .map((c) => ({ text: c, value: c }));
});

const dataGroupedByCarrierAndCategory = computed<CarrierCategoryGroupResult>(() => {
  if (!Array.isArray(carrierSurveyData.value)) {
    return {};
  }

  return carrierSurveyData.value.reduce(groupByCarrierAndCategory, {});
});

const chartData = computed<ChartDataType[]>(() => {
  const visibleCarriers = Object.keys(dataGroupedByCarrierAndCategory.value).filter(carrierVisible);

  const averages = visibleCarriers.reduce((carrierAveragesAcc, carrier) => {
    const carrierAverages: CarrierAverageByYear[] = [];

    // this is for comparison mode
    if (isComparing.value) {
      for (let year of appliedYears.value) {
        const average: CarrierAverageByYear = {
          year,
          avgX: avgForX(carrier, year),
          avgY: avgForY(carrier, year),
        };

        if (average.avgX + average.avgY > 0) {
          average.avgX = average.avgX > 0 ? average.avgX : minXValue.value;
          average.avgY = average.avgY > 0 ? average.avgY : minYValue.value;
        } else {
          average.avgX = 0;
          average.avgY = 0;
        }

        carrierAverages.push(average);
      }
    } else {
      // this is for non-comparison mode
      const average: CarrierAverageByYear = {
        year: undefined,
        avgX: avgForX(carrier),
        avgY: avgForY(carrier),
      };

      if (average.avgX + average.avgY > 0) {
        average.avgX = average.avgX > minXValue.value ? average.avgX : minXValue.value;
        average.avgY = average.avgY > minYValue.value ? average.avgY : minYValue.value;
      } else {
        average.avgX = 0;
        average.avgY = 0;
      }

      carrierAverages.push(average);
    }

    carrierAveragesAcc.set(carrier, carrierAverages);
    return carrierAveragesAcc;
  }, new Map<string, CarrierAverageByYear[]>());

  return visibleCarriers
    .flatMap((carrier: string) => {
      const carrierAverages = averages.get(carrier) ?? [];
      let filteredCarrierAverages = carrierAverages;

      if (!lockScale.value) {
        filteredCarrierAverages = carrierAverages.filter((average) => average.avgX > 0 && average.avgY > 0);
      }

      return filteredCarrierAverages.map((average) => {
        const carrierYears = yearsForCarrier(carrier);
        return {
          id: `${carrier}-${average.year ?? '0'}`,
          name: `${carrier}${props.scenario === SURVEY_TYPE_DNO && carrierHasNoYData(carrier) ? '*' : ''}${DATA_LABEL_PADDING}`,
          toolText: getToolTipText(carrier, viewOptions.value.bubbles, average.year),
          color: colorForCarrier(carrier, average.year),
          x: average.avgX,
          y: average.avgY,
          z: getBubbleSize(carrier, viewOptions.value.bubbles, average.year),
          showValue:
            !isComparing.value || carrierYears.length < 2 || average.year === getLatestYear(filteredCarrierAverages)
              ? showCarrierValue(carrier)
              : false,
        };
      });
    })
    .sort((a, b) => {
      // sorting by carrier and then year
      const carrierA = a.id.split('-')[0];
      const carrierB = b.id.split('-')[0];
      if (carrierA === carrierB) {
        return parseInt(a.id.split('-')[1]) - parseInt(b.id.split('-')[1]);
      }
      return carrierA.localeCompare(carrierB);
    });
});

function getLatestYear(averages: CarrierAverageByYear[]): number {
  return Math.max(...averages.map((a) => a.year ?? 0));
}

const chartYearLineAnnotations = computed(() => {
  const data = chartData.value;
  const annotations: AnnotationItemType[] = [];

  if (isComparing.value) {
    for (let i = 0; i < data.length - 1; i++) {
      const current = data[i];
      const next = data[i + 1];
      if (current.id.split('-')[0] === next.id.split('-')[0]) {
        annotations.push({
          id: `${Math.floor(Math.random() * 100000)}`,
          type: 'line',
          x: `$dataset.0.set.${i}.x`,
          y: `$dataset.0.set.${i}.y`,
          tox: `$dataset.0.set.${i + 1}.x`,
          toy: `$dataset.0.set.${i + 1}.y`,
          color: '#005da9',
          dashed: '1',
          thickness: '0.7',
        });
      }
    }
  }

  return annotations;
});

const tableData = computed(() => {
  if (!Array.isArray(carrierSurveyData.value)) {
    return [];
  }

  const data = carrierSurveyData.value.reduce((acc, d) => {
    if (!acc[d.category]) {
      acc[d.category] = {};
    }
    if (!acc[d.category][d.question]) {
      acc[d.category][d.question] = {};
    }
    if (!acc[d.category][d.question][d.type]) {
      acc[d.category][d.question][d.type] = {};
    }
    if (!acc[d.category][d.question][d.type][d.carrier]) {
      acc[d.category][d.question][d.type][d.carrier] = [];
    }
    acc[d.category][d.question][d.type][d.carrier].push(d);
    return acc;
  }, {} as CarrierDataTableType);

  const table: Row[] = [];
  for (const category of Object.keys(data)) {
    for (const question of Object.keys(data[category])) {
      for (const type of Object.keys(data[category][question])) {
        const typeName = type;
        const row: Row = {
          category,
          question,
          type: isComparing.value && typeName === 'Average Score' ? 'Net Change' : typeName,
          carriers: [] as CarrierRowData[],
        };
        for (const carrier of Object.keys(data[category][question][type])) {
          if (
            viewOptions.value.carriers === SHOW_SELECTED &&
            Array.isArray(appliedCarriers.value) &&
            appliedCarriers.value.length > 0 &&
            !appliedCarriers.value.includes(carrier)
          ) {
            continue;
          }

          // If in comparison mode, the new result must be the delta between the latest year and the earliest year
          const values = data[category][question][type][carrier];
          let value: number | undefined = undefined;

          if (isComparing.value) {
            const sortedData = values.sort((a, b) => (a.year && b.year ? a.year - b.year : 0));
            const earliestYearValue = sortedData[0]?.value;
            const latestYearValue = sortedData[sortedData.length - 1]?.value;

            if (latestYearValue && earliestYearValue) {
              value = latestYearValue - earliestYearValue;
            }
          } else {
            if (values.length === 1) {
              value = values[0].value ?? 0;
            }
          }

          const valueStr =
            value === undefined || (value <= 0 && !isComparing.value)
              ? 'N/A'
              : Number(value).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });

          let valueColor;
          if (isComparing.value) {
            if (value === undefined) {
              valueColor = colorScale.get('scaleGray')!;
            } else if (value === 0) {
              valueColor = 'transparent';
            } else if (value < 0) {
              valueColor = colorScale.get('deltaRed')!;
            } else {
              valueColor = colorScale.get('deltaGreen')!;
            }
          } else {
            valueColor =
              value == undefined || value <= 0
                ? colorScale.get('scaleGray')!
                : colorScaleFromValue(value, 5, 0, 'scale', 5);
          }

          row.carriers.push({
            key: `${category}_${question}_${type}_${carrier}`,
            sorter: carrier,
            name: carrierNameForTable(carrier),
            value: type === "Count of Yes's" || type === 'Total Responses' ? value : valueStr,
            bg:
              (type === "Count of Yes's" || type === 'Total Responses') && !isComparing.value
                ? 'transparent'
                : valueColor,
          });
        }
        row.carriers.sort((a, b) => a.sorter.localeCompare(b.sorter));
        table.push(row);
      }
    }
  }
  return table;
});

const tableDataCarriers = computed(() => {
  if (!Array.isArray(tableData.value) || tableData.value.length === 0) {
    return [];
  }
  return tableData.value[0].carriers.map((carrier) => carrier.name);
});

const minPremium = computed(() => {
  let minPremium: number | null = null;
  for (let carrier in dataGroupedByCarrierAndCategory.value) {
    let carrierData = Math.min(
      ...dataGroupedByCarrierAndCategory.value[carrier].overall.map((d) => d.totalPolicyPremium),
    );

    if (minPremium === null || carrierData < minPremium) {
      minPremium = carrierData;
    }
  }
  return minPremium;
});

const maxPremium = computed(() => {
  let maxPremium: number | null = null;
  for (let carrier in dataGroupedByCarrierAndCategory.value) {
    let carrierData = Math.max(
      ...dataGroupedByCarrierAndCategory.value[carrier].overall.map((d) => d.totalPolicyPremium),
    );
    if (maxPremium === null || carrierData > maxPremium) {
      maxPremium = carrierData;
    }
  }
  return maxPremium;
});

const minMaxValuePerQuestion = computed<Map<number, MinMaxValue[]>>(() => {
  let result = new Map<number, MinMaxValue[]>();
  for (let carrier in dataGroupedByCarrierAndCategory.value) {
    for (let question of dataGroupedByCarrierAndCategory.value[carrier].overall) {
      if (question.value !== undefined) {
        if (!result.has(question.displayOrder)) {
          result.set(question.displayOrder, [{ min: question.value, max: question.value, year: question.year }]);
        } else {
          let minMaxValue = result.get(question.displayOrder)?.find((d) => d.year === question.year);

          if (!minMaxValue) {
            result.get(question.displayOrder)?.push({
              min: question.value,
              max: question.value,
              year: question.year,
            });
          } else {
            if (minMaxValue && question.value < minMaxValue.min) {
              minMaxValue.min = question.value;
            }
            if (minMaxValue && question.value > minMaxValue.max) {
              minMaxValue.max = question.value;
            }
          }
        }
      }
    }
  }
  return result;
});

// methods
// --------
function exportChart() {
  const chartObj = chart.value?.chartObj;
  const [width, height] = [chartObj.width, chartObj.height];

  chartObj.resizeTo(900, 507);
  isExporting.value = true;

  setTimeout(() => {
    chartObj.exportChart({
      exportFormat: 'SVG',
    });

    chartObj.resizeTo(width, height);
    isExporting.value = false;
  }, 2000);
}

function carrierNameForTable(carrier: string) {
  if (viewOptions.value.labels === SHOW_NONE) {
    return '--';
  }
  if (viewOptions.value.labels === SHOW_SELECTED) {
    if (Array.isArray(appliedCarriers) && appliedCarriers.value.length > 0) {
      return appliedCarriers.value.includes(carrier) ? carrier : '--';
    }
  }
  return carrier;
}

function downloadToCSV() {
  DownloadTableToCSV('#survey-table', 'CarrierSurvey');
}

function updateViewOptions(
  labels: string,
  carriers: string,
  valueDisplay: string,
  bubbles: string,
  axisDisplayMode: AxisDisplayMode,
) {
  viewOptions.value = { labels, carriers, valueDisplay, bubbles, axisDisplayMode };
}

function carrierVisible(carrier: string) {
  if (viewOptions.value.carriers === SHOW_ALL) {
    return true;
  } else if (viewOptions.value.carriers === SHOW_SELECTED) {
    if (appliedCarriers.value.length > 0) {
      return appliedCarriers.value.includes(carrier);
    }
  }
  return true;
}

function showCarrierValue(carrier: string) {
  if (viewOptions.value.labels === SHOW_NONE) {
    return false;
  } else if (viewOptions.value.labels === SHOW_SELECTED) {
    if (appliedCarriers.value.length > 0) {
      return appliedCarriers.value.includes(carrier);
    }
  }
  return true;
}

function colorForCarrier(carrier: string, year?: number) {
  if (isComparing.value) {
    return bubbleChartColors[sortedYears.value.indexOf(year ?? 0)];
  }
  let isLatestYear = true;
  if (year) {
    isLatestYear = year === latestYear.value;
  }
  const color = isLatestYear ? chartQuaternaryColor : chartHighlightColor;

  if (appliedCarriers.value.length > 0) {
    return appliedCarriers.value.includes(carrier) ? color : chartMutedColor;
  }
  return color;
}

function yearsForCarrier(carrier: string) {
  if (!Array.isArray(carrierSurveyData.value)) {
    return [];
  }

  // group data by carrier only
  const dataByCarrier = carrierSurveyData.value.reduce(
    (acc, d) => {
      if (!acc[d.carrier]) {
        acc[d.carrier] = [];
      }
      acc[d.carrier].push(d);
      return acc;
    },
    {} as Record<string, CarrierSurveyResult[]>,
  );

  const carrierYears = dataByCarrier[carrier].reduce((acc, d) => {
    if (d.year && !acc.has(d.year)) {
      return acc.add(d.year);
    }
    return acc;
  }, new Set<number>());

  return Array.from(carrierYears.keys());
}

function applyCarrierGroupFilter() {
  appliedCarriers.value = selectedCarriers.value;
}

function applyYearFilter() {
  appliedYears.value = selectedYears.value;
  isComparing.value = false;
  getSurveyResults();
}

function compareYears() {
  appliedYears.value = selectedYears.value;
  isComparing.value = true;
  getSurveyResults();
}

function groupByCarrierAndCategory(acc: CarrierCategoryGroupResult, d: CarrierSurveyResult) {
  if (!acc[d.carrier]) {
    acc[d.carrier] = {};
  }
  if (!acc[d.carrier][d.category.toLowerCase()]) {
    acc[d.carrier][d.category.toLowerCase()] = [];
  }
  acc[d.carrier][d.category.toLowerCase()].push(d);

  return acc;
}

function sumByValue(acc: number, c: CarrierSurveyResult) {
  return Math.round((acc + (c.value ?? 0)) * 100) / 100;
}

function avgForX(carrier: string, year?: number) {
  const carrierData = dataGroupedByCarrierAndCategory.value[carrier];
  const averages = xAxisCategories.value
    .reduce((acc: CarrierSurveyResult[], c: string) => acc.concat(carrierData[c]), [])
    .filter((d: CarrierSurveyResult) => (year ? d.year === year : true))
    .filter(byAverageType)
    .filter((d: CarrierSurveyResult) => d.value !== undefined);

  if (!averages.length) {
    return 0;
  }

  return averages.reduce(sumByValue, 0) / averages.length;
}

function avgForY(carrier: string, year?: number) {
  const carrierData = dataGroupedByCarrierAndCategory.value[carrier];
  const averages = yAxisCategories.value
    .reduce((acc: CarrierSurveyResult[], c) => acc.concat(carrierData[c]), [])
    .filter((d: CarrierSurveyResult) => (year ? d.year === year : true))
    .filter(byAverageType)
    .filter((d: CarrierSurveyResult) => d.value !== undefined);

  if (!averages.length) {
    return 0;
  }
  return averages.reduce(sumByValue, 0) / averages.length;
}

function carrierHasNoYData(carrier: string): boolean {
  const claimsData = dataGroupedByCarrierAndCategory.value[carrier]?.['claims'] ?? [];
  const countOfYes = claimsData.find((d: CarrierSurveyResult) => d.type === "Count of Yes's");
  return !countOfYes?.value;
}

function getToolTipText(carrier: string, bubbleViewOption: string, year?: number) {
  if (bubbleViewOption === COUNT_BUBBLE) {
    return `${getCarrierData(carrier, 1, year).value}`;
  } else if (bubbleViewOption === PREMIUM_BUBBLE) {
    return `${compactMoney(getCarrierData(carrier, 1, year).totalPolicyPremium)}`;
  } else if (bubbleViewOption === PRIMARY_PUBLIC) {
    return `${getCarrierData(carrier, 2, year).value}`;
  } else if (bubbleViewOption === PRIMARY_PRIVATE) {
    return `${getCarrierData(carrier, 3, year).value}`;
  } else if (bubbleViewOption === LEAD_SIDE_A) {
    return `${getCarrierData(carrier, 4, year).value}`;
  } else if (bubbleViewOption === EXCESS_D_AND_O) {
    return `${getCarrierData(carrier, 5, year).value}`;
  } else if (bubbleViewOption === VALUE_CAC_PARTNERSHIP) {
    return `${getCarrierData(carrier, 20, year).value}`;
  } else {
    return `${carrier}`;
  }
}

function getBubbleSize(carrier: string, bubbleViewOption: string, year?: number) {
  if (bubbleViewOption === COUNT_BUBBLE) {
    return sizeByResponse(carrier, year);
  } else if (bubbleViewOption === PREMIUM_BUBBLE) {
    return sizeByPremium(carrier, year);
  } else if (bubbleViewOption === PRIMARY_PUBLIC) {
    return sizeByQuestion(carrier, 2, year);
  } else if (bubbleViewOption === PRIMARY_PRIVATE) {
    return sizeByQuestion(carrier, 3, year);
  } else if (bubbleViewOption === LEAD_SIDE_A) {
    return sizeByQuestion(carrier, 4, year);
  } else if (bubbleViewOption === EXCESS_D_AND_O) {
    return sizeByQuestion(carrier, 5, year);
  } else if (bubbleViewOption === VALUE_CAC_PARTNERSHIP) {
    return sizeByQuestion(carrier, 20, year);
  } else {
    return 1;
  }
}

function getCarrierData(carrier: string, displayOrder: number, year?: number): CarrierSurveyResult {
  const carrierData = dataGroupedByCarrierAndCategory.value[carrier];
  return carrierData['overall'].filter((d) => {
    if (year) {
      return d.displayOrder === displayOrder && d.year === year;
    }
    return d.displayOrder === displayOrder;
  })[0];
}

function sizeByResponse(carrier: string, year?: number) {
  const questionValue = getCarrierData(carrier, 1, year).value;
  const minMaxValue = minMaxValuePerQuestion.value.get(1)?.filter((d) => {
    return year ? d.year === year : true;
  })[0];

  if (!minMaxValue || !questionValue) {
    return 0;
  }

  const min = minMaxValue.min;
  const max = minMaxValue.max;

  return ((questionValue - min) / (max - min)) * 19 + 1;
}

function sizeByPremium(carrier: string, year?: number) {
  const vel = getCarrierData(carrier, 1, year).totalPolicyPremium;

  if (minPremium.value !== null && maxPremium.value != null) {
    let normalizedSize =
      1 + ((vel - minPremium.value) * (BUBBLE_SIZE_RANGE_TOP - 1)) / maxPremium.value - minPremium.value;
    return Math.round(normalizedSize);
  }

  return 0;
}

function sizeByQuestion(carrier: string, questionNumber: number, year?: number) {
  const questionValue = getCarrierData(carrier, questionNumber, year).value;

  const minMaxValue = minMaxValuePerQuestion.value.get(questionNumber)?.filter((d) => {
    return year ? d.year === year : true;
  })[0];

  if (minMaxValue && questionValue) {
    let normalizedSize =
      1 + ((questionValue - minMaxValue.min) * (BUBBLE_SIZE_RANGE_TOP - 1)) / (minMaxValue.max - minMaxValue.min);
    return Math.round(normalizedSize);
  }
  return 0;
}

function byAverageType(d: CarrierSurveyResult) {
  return d?.type.toLowerCase() === AVERAGE;
}

function getAverageFromArray(arr: number[]) {
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

async function getSurveyResults() {
  loading.value = true;
  if (isComparing.value) {
    carrierSurveyData.value = await carrierService.surveyResultsByYear(
      surveyType.value,
      appliedYears.value,
      selectedIndustries.value,
      selectedResponderFocuses.value,
    );
  } else {
    carrierSurveyData.value = await carrierService.carrierSurvey(
      surveyType.value,
      appliedYears.value,
      selectedIndustries.value,
      selectedResponderFocuses.value,
    );
  }

  if (industries.value.length === 0) {
    industries.value = (await referenceService.surveyResponseIndustries()).map((i) => ({
      label: i.value,
      value: i.id,
    }));
  }

  if (responderFocuses.value.length === 0) {
    responderFocuses.value = (await referenceService.surveyResponderFocuses()).map((i) => ({
      label: i.responderFocus,
      value: i.id,
    }));
  }

  computeQuadrants();

  loading.value = false;
}

function computeQuadrants() {
  if (lockScale.value) {
    // Static scale values
    minXValue.value = 0;
    maxXValue.value = 5;
    minYValue.value = 0;
    maxYValue.value = 5;
    avgXValue.value = 2.5;
    avgYValue.value = 2.5;
  } else {
    // Dynamic scale calculation
    switch (surveyType.value) {
      case SURVEY_TYPE_TL:
        minXValue.value = Math.min(...chartData.value.map((d) => d.x)) * 0.96;
        maxXValue.value = Math.max(...chartData.value.map((d) => d.x)) * 1.02;
        minYValue.value = Math.min(...chartData.value.map((d) => d.y)) * 0.88;
        maxYValue.value = Math.max(...chartData.value.map((d) => d.y)) * 1.05;
        avgXValue.value = getAverageFromArray(chartData.value.map((d) => d.x));
        avgYValue.value = getAverageFromArray(chartData.value.map((d) => d.y));
        break;
      default:
        minXValue.value = Math.min(...chartData.value.map((d) => d.x)) * 0.92;
        maxXValue.value = Math.max(...chartData.value.map((d) => d.x)) * 1.02;
        minYValue.value = Math.min(...chartData.value.map((d) => d.y)) * 0.95;
        maxYValue.value = Math.max(...chartData.value.map((d) => d.y)) * 1.086;
        avgXValue.value = getAverageFromArray(chartData.value.map((d) => d.x));
        avgYValue.value = getAverageFromArray(chartData.value.map((d) => d.y));
        break;
    }
  }
}

// watchers
// ----------------
watch(selectedIndustries, getSurveyResults);
watch(selectedCarriers, getSurveyResults);
watch(selectedResponderFocuses, getSurveyResults);

watch(lockScale, computeQuadrants);

// lifecycle hooks
// ----------------
onMounted(async () => {
  years.value = await carrierService.carrierSurveyYears(surveyType.value);
  if (props.scenario === SURVEY_TYPE_DNO && years.value.length > 0) {
    const mostRecentYear = [Math.max(...years.value)];
    selectedYears.value = mostRecentYear;
    appliedYears.value = mostRecentYear;
  } else {
    selectedYears.value = years.value;
    appliedYears.value = years.value;
  }

  await getSurveyResults();
});
</script>

<template>
  <div class="root">
    <div class="inner tw:flex tw:justify-between">
      <div class="header tw:flex tw:items-center tw:gap-1">
        <button @click="router.back()" class="tw:btn tw:flex tw:items-center tw:gap-2 tw:p-2 tw:btn-ghost tw:btn-sm">
          <ArrowLeft :size="16" />
        </button>
        <h1>{{ surveyTitle }}</h1>
      </div>
      <div v-if="props.scenario === 'tl'" class="tw:flex tw:flex-col tw:items-end">
        <a
          href="https://forms.office.com/pages/responsepage.aspx?id=Xywx9w0vJU2QVItm7hUYEl_jjmp_Ko1Div8LJuxrd0NUQ0gzQUwxV01LVUlJNTBWNk40MjlRNzVPOC4u"
          target="_blank"
          class="tw:btn tw:btn-sm tw:btn-primary">
          Survey Form
          <ExternalLink :size="16" class="tw:mb-0.5" />
        </a>
        <div class="tw:mt-1 tw:text-sm tw:text-gray-600 tw:italic">
          New responses will be added to results quarterly.
        </div>
      </div>
    </div>
    <div class="tw:ml-4 tw:flex tw:items-center">
      <CarrierGroupFilter v-model="selectedCarriers" :items="carriers" @update:model-value="applyCarrierGroupFilter" />

      <YearFilter v-model="selectedYears" :years="years" @aggregate="applyYearFilter" @compare="compareYears" />

      <GenericFilter
        v-if="props.scenario === 'tl'"
        title="Industry"
        chip-title-field="label"
        :types="industries"
        v-model="selectedIndustries" />

      <GenericFilter
        v-if="props.scenario === 'do'"
        title="Responder Focus"
        chip-title-field="label"
        :types="responderFocuses"
        v-model="selectedResponderFocuses" />

      <CarrierSurveyViewOptions
        :labels="viewOptions.labels"
        :carriers="viewOptions.carriers"
        :valueDisplay="viewOptions.valueDisplay"
        :bubbleSize="viewOptions.bubbles"
        :survey-type="surveyType"
        :applied-years="appliedYears"
        :axisDisplayMode="viewOptions.axisDisplayMode"
        @change="updateViewOptions" />

      <div class="tw:group tw:relative tw:items-center">
        <label for="lockScale" class="tw:gap-1/2 tw:label tw:ml-2 tw:flex tw:items-center">
          <input
            type="checkbox"
            id="lockScale"
            v-model="lockScale"
            class="tw:toggle tw:border tw:border-solid tw:border-gray-400 tw:toggle-md tw:toggle-secondary" />
          <span class="tw:cursor-pointer tw:pl-2 tw:select-none">Lock Scale</span>
        </label>

        <div
          class="tw:bottom--1 tw:pointer-events-none tw:absolute tw:left-3 tw:z-50 tw:w-[600px] tw:rounded-md tw:bg-base-200 tw:p-2 tw:opacity-0 tw:transition-opacity tw:group-hover:opacity-100">
          <b>OFF</b> - Quadrants and chart scale will be based on the selected data set's range and averages.<br />
          <b>ON</b> - Quadrants will be fixed at equal sizes.
        </div>
      </div>
    </div>

    <div class="inner">
      <main>
        <div v-if="isExporting" id="export-progress">
          <v-progress-linear :indeterminate="true" color="red" />
        </div>
        <div class="export-menu">
          <v-menu transition="none">
            <template v-slot:activator="{ props }">
              <v-btn
                color="white"
                elevation="0"
                variant="plain"
                size="x-small"
                style="border: thin solid #ccc"
                data-test="export-btn"
                v-bind="props">
                <Menu :size="16" color="grey" />
              </v-btn>
            </template>

            <v-list density="compact" elevation="0" class="tw:border-1 tw:border-solid">
              <v-list-item data-testid="download-as-image" @click="exportChart" title="Export for PowerPoint" />
            </v-list>
          </v-menu>
        </div>
        <div data-test="survey-chart" class="survey-chart">
          <h4 v-html="surveyResultsCaption" class="tw:ml-14 tw:font-sans"></h4>
          <div v-if="loading" class="tw:flex">
            <div class="tw:mx-auto tw:mt-[0.93rem] tw:h-[500px] tw:w-[96%] tw:skeleton tw:rounded-sm"></div>
          </div>
          <fusioncharts
            v-else
            v-show="!isExporting"
            ref="chart"
            type="bubble"
            width="100%"
            height="500"
            dataFormat="json"
            :dataSource="dataSource"></fusioncharts>
        </div>
      </main>
      <div class="tw:mt-16 tw:flex tw:items-center tw:gap-4">
        <h3>Raw Data</h3>
        <download-button :disabled="loading" @click="downloadToCSV" />
      </div>
      <div v-if="loading" class="tw:mt-3 tw:flex tw:flex-col tw:gap-4">
        <div v-for="n in 20" :key="n" class="tw:mx-auto tw:h-4 tw:w-full tw:skeleton"></div>
      </div>
      <div v-else class="survey-table-container">
        <table id="survey-table" class="apptable dense mt-4 survey-table" data-test="survey-table">
          <thead>
            <tr>
              <td class="text-right" :style="{ width: columnWidths[0] + 'ch' }">#</td>
              <td :style="{ width: columnWidths[1] + 'ch' }">Category</td>
              <td :style="{ width: columnWidths[2] + 'ch' }">Question</td>
              <td :style="{ width: columnWidths[3] + 'ch' }">Type</td>
              <td
                class="text-right carrier-column tw:whitespace-normal"
                v-for="(carrier, i) in tableDataCarriers"
                :key="i">
                {{ carrier }}
              </td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in tableData" :key="i">
              <td class="text-right">{{ i + 1 }}</td>
              <td>{{ row.category }}</td>
              <td>
                <div class="tw:text-wrap">
                  {{ row.question }}
                </div>
              </td>
              <td>{{ row.type }}</td>
              <td
                class="text-right carrier-column"
                v-for="carrier in row.carriers"
                :key="carrier.key"
                :style="{ backgroundColor: carrier.bg }">
                {{ showValues ? carrier.value : '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.survey-table-container {
  overflow-x: scroll;
}

.survey-table {
  table-layout: fixed;
}

.survey-table > thead > tr > td,
.survey-table > tbody > tr > td {
  white-space: nowrap;
}

.survey-table > thead > tr > td {
  width: 5rem;
  overflow: hidden;
}

#export-progress {
  width: 100%;
  height: 500px;
  padding: 10%;
}

.export-menu {
  display: grid;
  justify-items: end;
  padding-right: 1rem;
  position: relative;
  top: 1.25rem;
  z-index: 1;
}
</style>
